import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  loadMealsConfiguration,
  saveMealsConfiguration,
  resetMealsConfiguration,
  getEnabledMeals,
  getMealTitle,
  getDefaultMealIdForCurrentTime,
  asMealId,
  DefaultMealTimeRangeForId,
  DefaultMealTypeForId,
  type MealsConfiguration,
} from "@/services/meals";
import type { Meal, MealId, MealType } from '@macro-tracker/models';

export type { Meal, MealId, MealType, MealsConfiguration };
export {
  getDefaultMealIdForCurrentTime,
  getMealTitle,
  getEnabledMeals,
  asMealId,
  DefaultMealTimeRangeForId,
  DefaultMealTypeForId,
};

export const useMealsStore = defineStore("meals", () => {
  const configuration = ref<MealsConfiguration | null>(null);
  const isLoading = ref(false);

  const meals = computed(() => configuration.value?.meals ?? []);
  const enabledMeals = computed(() => getEnabledMeals(meals.value));

  async function load(): Promise<MealsConfiguration> {
    if (configuration.value) return configuration.value;
    isLoading.value = true;
    try {
      configuration.value = await loadMealsConfiguration();
      return configuration.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function reload(): Promise<MealsConfiguration> {
    isLoading.value = true;
    try {
      configuration.value = await loadMealsConfiguration();
      return configuration.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function save(updatedMeals: Meal[]): Promise<void> {
    await saveMealsConfiguration(updatedMeals);
    await reload();
  }

  async function reset(): Promise<void> {
    configuration.value = await resetMealsConfiguration();
  }

  function getDefaultMealId(options?: { now?: Date; timeZone?: string }): MealId | null {
    return getDefaultMealIdForCurrentTime(meals.value, options);
  }

  function getMealLabel(mealId: MealId): string {
    return getMealTitle(meals.value, mealId);
  }

  return {
    configuration,
    isLoading,
    meals,
    enabledMeals,
    load,
    reload,
    save,
    reset,
    getDefaultMealId,
    getMealLabel,
  };
});
